import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDebounce } from 'react-use'

import { CALL_REMINDER_IN_MINUTES } from '~/constants/call'
import { ERROR_MESSAGES } from '~/constants/messages'
import useCall from '~/hooks/useCall'
import useDebugger from '~/hooks/useDebugger'
import pickup from '~/services/pickup'
import type { CustomerData } from '~/types/call'

import Button from '../common/Button'
import Input from '../common/Input'
import Textarea from '../common/Textarea'
import ModalCallReminder from './ModalCallReminder'
import ModalSuratKuasa from './ModalSuratKuasa'

const CurrentServing = () => {
  const { debug } = useDebugger()
  const {
    isQueueStarted,
    currentServing,
    loadingStates,
    getCustomer,
    resetCustomer,
    updateCustomer,
    formRef,
    callData,
    isCallReminderOpen,
    setIsCallReminderOpen,
    auxQueue,
  } = useCall()
  const [custNo, setCustNo] = useState('')
  const [timer, setTimer] = useState(0)
  const [isSuratKuasaOpen, setIsSuratKuasaOpen] = useState(false)

  const { queue_id, queue_no, counter_lane, created_at, queue_secret_code, start_service, queue_necessary } =
    currentServing.ticket

  // Only queues born from a Dikuasakan kode unik have a Surat Kuasa; for every
  // other queue the endpoint 404s, which is the normal case - hence no retry
  // and no error toast, the button simply stays hidden.
  const suratKuasaQuery = useQuery({
    queryKey: ['surat-kuasa-by-queue', queue_id],
    queryFn: () => pickup.suratKuasaByQueue({ queue_id }),
    enabled: !!queue_id && currentServing.isIdle,
    retry: false,
    staleTime: Infinity,
    // Swallowed on purpose (404 = not a Surat Kuasa queue, and a jba-web-api
    // outage must not break serving) - logged so it's still traceable with
    // VITE_DEBUG on.
    onError: (error) => debug('info', 'API `pickup/surat-kuasa-by-queue` failed:', error),
  })

  // surat_kuasa_id is the proof this really is a Surat Kuasa payload - guards
  // against a truthy-but-empty data field turning the button on with nothing
  // behind it.
  const suratKuasa = suratKuasaQuery.data?.data?.surat_kuasa_id ? suratKuasaQuery.data.data : undefined
  const { customer_no, customer_fullname, customer_email } = currentServing.customer

  const initialTimeDifference = start_service ? dayjs().diff(dayjs(start_service), 'second') : 0

  const formatTimer = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${(seconds % 60)
      .toString()
      .padStart(2, '0')}`
    return formattedTime
  }

  const {
    handleSubmit,
    control,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<CustomerData>()

  const prevCustomerRef = useRef(currentServing.customer)

  useDebounce(
    () => {
      if (custNo) {
        getCustomer(custNo)
      } else {
        resetCustomer()
      }
    },
    1000,
    [custNo],
  )

  useEffect(() => {
    if (JSON.stringify(prevCustomerRef.current) !== JSON.stringify(currentServing.customer)) {
      reset(currentServing.customer)

      prevCustomerRef.current = currentServing.customer
    }
  }, [currentServing.customer, reset])

  useEffect(() => {
    let interval: number = 0

    if (currentServing.isServing) {
      setTimer(initialTimeDifference)

      interval = window.setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1)
      }, 1000)
    } else {
      setTimer(0)
      clearInterval(interval)
    }

    return () => {
      clearInterval(interval)
    }
  }, [currentServing.isServing, initialTimeDifference])

  useEffect(() => {
    const shouldShowReminder = timer % (60 * Number(CALL_REMINDER_IN_MINUTES)) === 0

    if (shouldShowReminder && timer > 0) {
      setIsCallReminderOpen(true)
    }
  }, [timer, setIsCallReminderOpen])

  // temporary solution to clear field errors when skip

  useEffect(() => {
    if (callData.skip_list.length > 0) {
      clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callData.skip_list])

  useEffect(() => {
    setIsSuratKuasaOpen(false)
  }, [queue_id])

  const onSubmit = () => {
    auxQueue()
    setIsCallReminderOpen(false)
  }

  const handleConfirmFinished = () => {
    if (formRef.current) {
      formRef.current.requestSubmit()
      setIsCallReminderOpen(false)
    }
  }

  const handleCloseCallReminder = () => {
    setIsCallReminderOpen(false)
  }

  return (
    <>
      <ModalCallReminder
        isOpen={isCallReminderOpen}
        closeModal={handleCloseCallReminder}
        confirmFinished={handleConfirmFinished}
      />
      {suratKuasa && (
        <ModalSuratKuasa isOpen={isSuratKuasaOpen} closeModal={() => setIsSuratKuasaOpen(false)} data={suratKuasa} />
      )}
      <div className="flex min-h-[calc(100%-6rem)] flex-col justify-between rounded-lg bg-white p-4">
        {isQueueStarted ? (
          <>
            {currentServing.isIdle ? (
              <>
                <div className="mb-2 space-y-0.5 text-center font-bold text-jba-blue">
                  <div>Current Serving</div>
                  <div className="inline-flex items-center gap-2 text-xl">
                    {queue_no} - {queue_secret_code}
                  </div>

                  <div className="text-sm">Services Counter {counter_lane}</div>
                  <div className="text-xs">Issue Date: {dayjs(created_at).format('DD-MM-YYYY HH:mm:ss')}</div>
                  <div className="inline-block w-28 rounded-md bg-gray-200 p-1 text-center font-mono text-sm">
                    {formatTimer(timer)}
                  </div>
                </div>
                <form ref={formRef} className="space-y-3 overflow-y-scroll" onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex items-center gap-4">
                    <label htmlFor="noCust" className="mb-1 block w-20 whitespace-nowrap text-xs text-jba-blue">
                      No. Cust
                    </label>
                    <Controller
                      name="customer_no"
                      control={control}
                      defaultValue={customer_no || ''}
                      render={({ field }) => (
                        <Input
                          type="text"
                          size="small"
                          value={field.value}
                          isLoading={loadingStates.customer}
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            updateCustomer({ ...currentServing.customer, customer_no: e.target.value })
                            setCustNo(e.target.value)
                          }}
                          errorMessage={errors.customer_no?.message}
                        />
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label htmlFor="nama" className="mb-1 block w-20 whitespace-nowrap text-xs text-jba-blue">
                      Nama
                    </label>
                    <Controller
                      name="customer_fullname"
                      control={control}
                      defaultValue={customer_fullname || ''}
                      render={({ field }) => (
                        <Input
                          type="text"
                          size="small"
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            updateCustomer({ ...currentServing.customer, customer_fullname: e.target.value })
                          }}
                          errorMessage={errors.customer_fullname?.message}
                        />
                      )}
                      rules={{ required: ERROR_MESSAGES.REQUIRED_NAME }}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label htmlFor="email" className="mb-1 block w-20 whitespace-nowrap text-xs text-jba-blue">
                      Email
                    </label>
                    <Controller
                      name="customer_email"
                      control={control}
                      defaultValue={customer_email || ''}
                      render={({ field }) => (
                        <Input
                          type="text"
                          size="small"
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            updateCustomer({ ...currentServing.customer, customer_email: e.target.value })
                          }}
                          errorMessage={errors.customer_email?.message}
                        />
                      )}
                      rules={{
                        required: ERROR_MESSAGES.REQUIRED_EMAIL,
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                          message: ERROR_MESSAGES.INVALID_EMAIL,
                        },
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label htmlFor="keterangan" className="mb-1 block whitespace-nowrap text-xs text-jba-blue">
                      Keterangan
                    </label>
                    <Controller
                      name="customer_keterangan"
                      control={control}
                      defaultValue={currentServing.customer.customer_keterangan || ''}
                      render={({ field }) => (
                        <Textarea
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            updateCustomer({ ...currentServing.customer, customer_keterangan: e.target.value })
                          }}
                          errorMessage={errors.customer_keterangan?.message}
                        />
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label htmlFor="keperluan" className="mb-1 block w-20 whitespace-nowrap text-xs text-jba-blue">
                      Keperluan
                    </label>
                    <div className="w-full">
                      <Input type="text" size="small" value={queue_necessary || '-'} disabled />
                    </div>
                  </div>
                  {suratKuasa && (
                    <div className="flex items-start gap-4">
                      <div className="w-20 shrink-0" />
                      <div className="space-y-1">
                        <Button
                          type="button"
                          variant="primary"
                          size="small"
                          icon="material-symbols:description-outline"
                          onClick={() => setIsSuratKuasaOpen(true)}
                        >
                          <span className="text-xs">Lihat Surat Kuasa</span>
                        </Button>
                        <div className="text-[11px] leading-tight text-gray-600">
                          Unit/dokumen antrean ini diambil orang lain (Dikuasakan). Cek KTP penerima kuasa sebelum
                          dilayani.
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-gray-400">
                Pilih antrean yang akan dilayani.
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">Antrean belum dimulai.</div>
        )}
      </div>
    </>
  )
}

export default CurrentServing
